import { ApolloError } from "@apollo/client";
import { Save } from "@mui/icons-material";
import { Alert, Stack, TextField, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { SelectElement, SwitchElement } from "react-hook-form-mui";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router";

import { GqlFeatureType, GqlMapShapeIcon } from "~/__generated__";
import GaiaConfirmCloseDialog from "~/modules/app/components/GaiaConfirmCloseDialog";
import GaiaHookFormSubmitButton from "~/modules/app/components/GaiaHookFormSubmitButton";
import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";
import MapShapeIconPicker from "~/modules/map/icons/components/MapShapeIconPicker";

type Props = {
    initialData?: FunctionTypeFormInputs;
    onSubmit: (value: FunctionTypeFormInputs) => void;
    error?: ApolloError;
};

export default function FunctionTypeForm({ onSubmit, error, initialData }: Props) {
    const { t } = useTranslation("functionType");

    const {
        handleSubmit,
        register,
        setError,
        control,
        formState: { errors, isDirty, isSubmitted, isSubmitting },
    } = useForm<FunctionTypeFormInputs>({
        defaultValues: {
            name: initialData?.name ?? "",
            description: initialData?.description ?? "",
            featureType: initialData?.featureType ?? GqlFeatureType.LineString,
            mapIcon: initialData?.mapIcon ?? GqlMapShapeIcon.Circle,
            isEssential: initialData?.isEssential ?? false,
        },
    });

    const blocker = useBlocker(!isSubmitting && !isSubmitted && isDirty);

    useApplyErrorToForm(error, setError);

    const featureTypeOptions = Object.values(GqlFeatureType).map(value => ({
        id: value,
        label: t(`functionTypeForm.label${value}`),
    }));

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <GaiaConfirmCloseDialog blocker={blocker} />
            <Stack gap={2}>
                {errors.root && (
                    <Alert severity="error">
                        <Typography>{errors.root.message}</Typography>
                    </Alert>
                )}

                <TextField
                    {...register("name")}
                    label={t("functionTypeForm.labelName")}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                />
                <TextField
                    {...register("description")}
                    label={t("functionTypeForm.labelDescription")}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    minRows={5}
                    multiline
                />
                <SelectElement
                    control={control}
                    label={t("functionTypeForm.labelFeatureType")}
                    rules={{ required: true }}
                    name="featureType"
                    valueKey="id"
                    options={featureTypeOptions}
                />
                <SwitchElement name="isEssential" label={t("functionTypeForm.labelIsEssential")} control={control} />
                <MapShapeIconPicker name="mapIcon" label={t("functionTypeForm.labelMapIcon")} control={control} />

                <GaiaHookFormSubmitButton control={control} startIcon={<Save />}>
                    {t("functionTypeForm.btnSubmit")}
                </GaiaHookFormSubmitButton>
            </Stack>
        </form>
    );
}

export type FunctionTypeFormInputs = {
    name: string;
    description?: string | null;
    featureType: GqlFeatureType;
    mapIcon: GqlMapShapeIcon;
    isEssential: boolean;
};
